/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CustomTerminology } from '../models/CustomTerminology';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CustomTerminologiesService {

    /**
     * List Custom Terminologies
     * Returns a list of custom terminologies for the account
     * @param accountExternalId Account External ID
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiCustomTerminologiesTerminologies(
        accountExternalId: string,
    ): CancelablePromise<{
        custom_terminologies?: Array<CustomTerminology>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/custom_terminologies/{account_external_id}/terminologies',
            path: {
                'account_external_id': accountExternalId,
            },
        });
    }

    /**
     * Create Custom Terminology
     * @param accountExternalId Account External ID
     * @param requestBody CSV
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiCustomTerminologiesTerminologies(
        accountExternalId: string,
        requestBody?: string,
    ): CancelablePromise<{
        custom_terminology: CustomTerminology;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/custom_terminologies/{account_external_id}/terminologies',
            path: {
                'account_external_id': accountExternalId,
            },
            body: requestBody,
            mediaType: 'text/csv',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * Update Custom Terminology
     * @param accountExternalId Account External ID
     * @param id Terminology ID
     * @param requestBody CSV
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiCustomTerminologiesTerminologies(
        accountExternalId: string,
        id: string,
        requestBody?: string,
    ): CancelablePromise<{
        custom_terminology: CustomTerminology;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/custom_terminologies/{account_external_id}/terminologies/{id}',
            path: {
                'account_external_id': accountExternalId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'text/csv',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * Delete Custom Terminology
     * Deletes a custom terminology
     * @param accountExternalId Account External ID
     * @param id Terminology ID
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiCustomTerminologiesTerminologies(
        accountExternalId: string,
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/custom_terminologies/{account_external_id}/terminologies/{id}',
            path: {
                'account_external_id': accountExternalId,
                'id': id,
            },
        });
    }

    /**
     * Get Download URL for Custom Terminology
     * Returns a presigned URL to download the custom terminology CSV file
     * @param accountExternalId Account External ID
     * @param id Terminology ID
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiCustomTerminologiesTerminologiesDownloadUrl(
        accountExternalId: string,
        id: string,
    ): CancelablePromise<{
        /**
         * Presigned S3 download URL
         */
        url: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/custom_terminologies/{account_external_id}/terminologies/{id}/download_url',
            path: {
                'account_external_id': accountExternalId,
                'id': id,
            },
            errors: {
                422: `failed to generate URL`,
            },
        });
    }

    /**
     * List custom terminologies
     * @param xAccessToken API Token
     * @returns any custom terminologies retrieved
     * @throws ApiError
     */
    public static getInternalApiWxCustomTerminologies(
        xAccessToken?: string,
    ): CancelablePromise<{
        custom_terminologies?: Array<CustomTerminology>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/custom_terminologies',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                401: `unauthorized`,
            },
        });
    }

    /**
     * Creates a custom terminology
     * @param xAccessToken API Token
     * @param contentType Content type
     * @param requestBody CSV
     * @returns any custom terminology created
     * @throws ApiError
     */
    public static postInternalApiWxCustomTerminologies(
        xAccessToken?: string,
        contentType?: string,
        requestBody?: string,
    ): CancelablePromise<{
        custom_terminology?: CustomTerminology;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/custom_terminologies',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
                'Content-Type': contentType,
            },
            body: requestBody,
            mediaType: 'text/csv',
            errors: {
                401: `unauthorized`,
                415: `unsupported media type`,
                422: `invalid request`,
            },
        });
    }

    /**
     * Updates a custom terminology
     * @param id Custom Terminology External ID
     * @param xAccessToken API Token
     * @param contentType Content type
     * @param requestBody CSV
     * @returns any custom terminology updated
     * @throws ApiError
     */
    public static patchInternalApiWxCustomTerminologies(
        id: string,
        xAccessToken?: string,
        contentType?: string,
        requestBody?: string,
    ): CancelablePromise<{
        custom_terminology?: CustomTerminology;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/wx/custom_terminologies/{id}',
            path: {
                'id': id,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
                'Content-Type': contentType,
            },
            body: requestBody,
            mediaType: 'text/csv',
            errors: {
                401: `unauthorized`,
                404: `terminology not found`,
                415: `unsupported media type`,
            },
        });
    }

    /**
     * Deletes a custom terminology
     * @param id Custom Terminology External ID
     * @param xAccessToken API Token
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiWxCustomTerminologies(
        id: string,
        xAccessToken?: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/wx/custom_terminologies/{id}',
            path: {
                'id': id,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                401: `unauthorized`,
                404: `terminology not found`,
            },
        });
    }

    /**
     * Gets download URL for a custom terminology
     * @param id Custom Terminology External ID
     * @param xAccessToken API Token
     * @returns any download URL retrieved
     * @throws ApiError
     */
    public static getInternalApiWxCustomTerminologiesDownloadUrl(
        id: string,
        xAccessToken?: string,
    ): CancelablePromise<{
        url?: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/custom_terminologies/{id}/download_url',
            path: {
                'id': id,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                401: `unauthorized`,
                404: `terminology not found`,
                422: `failed to generate download URL`,
            },
        });
    }

}